import React, { useState } from 'react';
import {
  Collapse,
  Tag,
  Typography,
  Row,
  Col,
  Button,
  Divider,
  Tooltip,
  Space,
  Alert,
} from 'antd';
import {
  WarningOutlined,
  BulbOutlined,
  TeamOutlined,
  RiseOutlined,
  AimOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LikeOutlined,
  DislikeOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

const { Panel } = Collapse;
const { Text, Title } = Typography;

const AiSummaryCard = ({ aiSummary, instanceData }) => {
  const [feedback, setFeedback] = useState(null);

  if (!aiSummary || !aiSummary.content || aiSummary.status !== 'success') {
    return null;
  }

  const content = aiSummary.content;

  const handleFeedback = (type) => {
    setFeedback(type);
    // Feedback is tracked via Slack buttons primarily
    // This is a visual-only acknowledgement in web app
  };

  const renderBlockers = () => {
    if (!content.blockers || content.blockers.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ color: '#cf1322', fontSize: 14 }}>
          <WarningOutlined style={{ marginRight: 6 }} />
          Blockers & Risks
        </Text>
        <div style={{ marginTop: 8 }}>
          {content.blockers.map((blocker, i) => (
            <div
              key={i}
              style={{
                padding: '8px 12px',
                marginBottom: 6,
                background: '#fff2f0',
                borderLeft: '3px solid #ff4d4f',
                borderRadius: '0 4px 4px 0',
              }}
            >
              <Text>{blocker.text}</Text>
              {blocker.user && (
                <Tag style={{ marginLeft: 8 }} color="default">
                  {blocker.user}
                </Tag>
              )}
              {blocker.recurring && (
                <Tag color="red" style={{ marginLeft: 4 }}>
                  {blocker.dayCount
                    ? `${blocker.dayCount} days`
                    : 'Recurring'}
                </Tag>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderHighlights = () => {
    if (!content.highlights || content.highlights.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 14 }}>
          <BulbOutlined style={{ marginRight: 6, color: '#faad14' }} />
          Key Highlights
        </Text>
        <ul style={{ marginTop: 8, paddingLeft: 20, marginBottom: 0 }}>
          {content.highlights.map((highlight, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              <Text>{highlight}</Text>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderJiraSnapshot = () => {
    if (!content.jiraSnapshot) return null;
    const snap = content.jiraSnapshot;
    const hasDone = snap.done && snap.done.length > 0;
    const hasStale = snap.stale && snap.stale.length > 0;
    if (!hasDone && !hasStale && !snap.sprintPace) return null;

    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 14 }}>
          <ThunderboltOutlined style={{ marginRight: 6, color: '#1890ff' }} />
          Jira Snapshot
        </Text>
        <div style={{ marginTop: 8 }}>
          {hasDone && (
            <div style={{ marginBottom: 4 }}>
              <CheckCircleOutlined
                style={{ color: '#52c41a', marginRight: 6 }}
              />
              <Text type="secondary">Done: </Text>
              <Text>{snap.done.join(', ')}</Text>
            </div>
          )}
          {hasStale && (
            <div style={{ marginBottom: 4 }}>
              <ClockCircleOutlined
                style={{ color: '#faad14', marginRight: 6 }}
              />
              <Text type="secondary">Stale: </Text>
              <Text>{snap.stale.join(', ')}</Text>
            </div>
          )}
          {snap.sprintPace && (
            <div>
              <RiseOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text type="secondary">Sprint pace: </Text>
              <Tag
                color={
                  snap.sprintPace === 'on track'
                    ? 'green'
                    : snap.sprintPace === 'ahead'
                      ? 'blue'
                      : 'orange'
                }
              >
                {snap.sprintPace}
              </Tag>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderParticipation = () => {
    if (!content.participation) return null;
    const p = content.participation;
    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 14 }}>
          <TeamOutlined style={{ marginRight: 6, color: '#722ed1' }} />
          Participation
        </Text>
        <div style={{ marginTop: 8 }}>
          <Space size={[8, 8]} wrap>
            <Tag color="green">
              {p.responded || 0}/{p.total || 0} responded
            </Tag>
            {p.skipped > 0 && <Tag color="orange">{p.skipped} skipped</Tag>}
            {p.onLeave > 0 && <Tag color="blue">{p.onLeave} on leave</Tag>}
            {p.late > 0 && <Tag color="gold">{p.late} late</Tag>}
          </Space>
        </div>
      </div>
    );
  };

  const renderPatterns = () => {
    if (!content.patterns || content.patterns.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 14 }}>
          <RiseOutlined style={{ marginRight: 6, color: '#eb2f96' }} />
          Patterns & Trends
        </Text>
        <div style={{ marginTop: 8 }}>
          {content.patterns.map((pattern, i) => (
            <Alert
              key={i}
              message={pattern.text}
              type={
                pattern.type === 'recurring_blocker'
                  ? 'error'
                  : pattern.type === 'participation_drop'
                    ? 'warning'
                    : 'info'
              }
              showIcon
              style={{ marginBottom: 6 }}
              banner
            />
          ))}
        </div>
      </div>
    );
  };

  const renderCustomFocus = () => {
    if (!content.customFocus) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 14 }}>
          <AimOutlined style={{ marginRight: 6, color: '#13c2c2' }} />
          Custom Focus
        </Text>
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            background: '#e6fffb',
            borderLeft: '3px solid #13c2c2',
            borderRadius: '0 4px 4px 0',
          }}
        >
          <Text>{content.customFocus}</Text>
        </div>
      </div>
    );
  };

  const renderDataSourceInfo = () => {
    const parts = [];
    if (content.participation && content.participation.total) {
      parts.push(`${content.participation.total} participants`);
    }
    if (content.jiraSnapshot) {
      parts.push('Jira activity included');
    }
    if (content.patterns && content.patterns.length > 0) {
      parts.push('Cross-session patterns analyzed');
    }
    if (content.customFocus) {
      parts.push('Custom focus prompt active');
    }

    if (parts.length === 0) return null;

    return (
      <Collapse ghost style={{ marginTop: 8 }}>
        <Panel
          header={
            <Text type="secondary" style={{ fontSize: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              What data was used
            </Text>
          }
          key="data-source"
          style={{ padding: 0 }}
        >
          <ul
            style={{
              paddingLeft: 20,
              marginBottom: 0,
              fontSize: 12,
              color: '#8c8c8c',
            }}
          >
            {parts.map((part, i) => (
              <li key={i}>{part}</li>
            ))}
            {aiSummary.model && <li>Model: {aiSummary.model}</li>}
            {aiSummary.generatedAt && (
              <li>
                Generated:{' '}
                {new Date(aiSummary.generatedAt).toLocaleTimeString()}
              </li>
            )}
          </ul>
        </Panel>
      </Collapse>
    );
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Collapse
        defaultActiveKey={['ai-summary']}
        style={{
          borderLeft: '3px solid #722ed1',
          borderRadius: 4,
          background: '#faf5ff',
        }}
      >
        <Panel
          header={
            <span style={{ fontWeight: 600, color: '#722ed1' }}>
              ✨ AI Summary
            </span>
          }
          key="ai-summary"
        >
          {renderBlockers()}
          {renderHighlights()}
          {renderJiraSnapshot()}
          {renderParticipation()}
          {renderPatterns()}
          {renderCustomFocus()}

          <Divider style={{ margin: '12px 0' }} />

          {/* Feedback widget */}
          <Row justify="space-between" align="middle">
            <Col>
              {renderDataSourceInfo()}
            </Col>
            <Col>
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Was this helpful?
                </Text>
                <Tooltip title="Helpful">
                  <Button
                    type={feedback === 'positive' ? 'primary' : 'text'}
                    size="small"
                    icon={<LikeOutlined />}
                    onClick={() => handleFeedback('positive')}
                    style={
                      feedback === 'positive'
                        ? { background: '#52c41a', borderColor: '#52c41a' }
                        : {}
                    }
                  />
                </Tooltip>
                <Tooltip title="Not helpful">
                  <Button
                    type={feedback === 'negative' ? 'primary' : 'text'}
                    size="small"
                    icon={<DislikeOutlined />}
                    onClick={() => handleFeedback('negative')}
                    style={
                      feedback === 'negative'
                        ? { background: '#ff4d4f', borderColor: '#ff4d4f' }
                        : {}
                    }
                  />
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </Panel>
      </Collapse>
    </div>
  );
};

export default AiSummaryCard;
